import { User } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import {
  buildBondOptionTradeMessage,
  returnMissingBondOptionTicketFields,
  returnSentBondOptionTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/bondOptionTicketHelper";
import {
  buildSwaptionTradeMessage,
  returnMissingSwaptionTicketFields,
  returnSentSwaptionTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/swaptionTicketHelper";
import {
  buildSwapTradeMessage,
  returnMissingSwapTicketFields,
  returnSentSwapTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/swapTicketHelper";
import {
  buildBondTradeMessage,
  returnMissingBondTicketFields,
  returnSentTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/bondTicketHelper";
import {
  buildFutureTradeMessage,
  returnMissingFutureTicketFields,
  returnSentFutureTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/futureTicketHelper";
import { ITradeMessage } from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import {
  IBondOptionTradeMessage,
  ISwaptionTradeMessage,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { ISwapTradeMessage } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { IRepoTradeMessage } from "@/lib/interfaces and Enums/ENUMRepoTradeMessage";
import {
  buildRepoTradeMessage,
  returnMissingRepoTicketFields,
  returnSentRepoTicketSummary,
} from "@/components/widgets/tradeFromScratch/helper/repoTicketHelper";
import {
  IDatedTicket,
  returnRolledTradeDateChanges,
  returnTodayISODate,
} from "@/lib/businessRules/settlementDateRules";
import { logClientError } from "@/lib/errorLoggingACTIONS";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { returnTraderName, returnUserEntity } from "@/lib/userIdentityHelper";
import { fetchRequestWrapper } from "@/lib/utilities";
import { findBondSecurityByIsin } from "./bondSecuritiesStoreACTIONS";
import { refreshUnassignedBlocks } from "./importBlocksAndAllocationsStoreACTIONS";
import { findPortfolioByCode } from "./portfoliosStoreACTIONS";
import tradeFromScratchStore, { TicketInstrument } from "./tradeFromScratchStore";

// LQC-2254 / LQC-2495 - the write side of Trade From Scratch. Sends the ticket through
// the same door an OMS uses; the back end turns it into an unassigned block, which the
// Trade Creation widget then treats like any other.

export const BOND_TRADE_ENDPOINT = "/Trade/Bond";
export const FUTURE_TRADE_ENDPOINT = "/Trade/Future";
export const BOND_OPTION_TRADE_ENDPOINT = "/Trade/BondOption";
export const SWAP_TRADE_ENDPOINT = "/Trade/Swap";
export const SWAPTION_TRADE_ENDPOINT = "/Trade/Swaption";
export const REPO_TRADE_ENDPOINT = "/Trade/RepurchaseAgreement";

export interface ISendBondTicketParams {
  // The ticket's store key (widget cryptoID or a Trade Creation draft id).
  ticketId: string;
  user: User | undefined;
  getAccessTokenSilently: Function;
}

interface IPostTicketParams extends ISendBondTicketParams {
  endpoint: string;
  // Everything the instrument decides, resolved by the caller before the shared path.
  missingFields: string[];
  message:
    | ITradeMessage
    | IBondOptionTradeMessage
    | ISwapTradeMessage
    | ISwaptionTradeMessage
    | IRepoTradeMessage
    | undefined;
  summary: string;
  successMessage: string;
  source: string;
  resetTicket: () => void;
}

// A send that never comes back leaves the ticket on "Sending ..." with no way out but a
// reload: a pricing failure inside the API can hang the response rather than erroring
// (LQC-2676). Generous, because the POST itself does real work.
export const TICKET_SEND_TIMEOUT_MS = 30000;

// The parts of a send that do not vary by instrument: the busy flag, the POST, the
// confirmation and the unassigned-blocks refresh.
async function postTicket({
  ticketId,
  getAccessTokenSilently,
  endpoint,
  missingFields,
  message,
  summary,
  successMessage,
  source,
  resetTicket,
}: IPostTicketParams): Promise<boolean> {
  if (missingFields.length > 0) {
    toast.error(`Cannot send - missing ${missingFields.join(", ")}.`);
    return false;
  }
  if (!message) {
    toast.error("Cannot send - the selected portfolio could not be resolved.");
    return false;
  }

  const store = tradeFromScratchStore.getState();
  store.setIsSendingTicket(ticketId, true);
  try {
    await fetchRequestWrapper(
      {
        url: endpoint,
        method: "POST",
        objToSend: { ...message, isManual: true },
        timeoutMs: TICKET_SEND_TIMEOUT_MS,
      },
      getAccessTokenSilently,
    );
    toast.success(successMessage);
    resetTicket();
    // The toast is bottom-right and gone in two seconds, and a cleared ticket looks the
    // same as one the user cleared themselves - the widget confirms the send itself.
    tradeFromScratchStore
      .getState()
      .setLastSentTicket(ticketId, { summary, sentAt: Date.now() });
    // The new block only reaches the grid because we pull it: no SignalR event adds an
    // unseen block id. See refreshUnassignedBlocks.
    await refreshUnassignedBlocks(getAccessTokenSilently);
    return true;
  } catch (error) {
    // fetchRequestWrapper has already toasted the failure - keep the ticket intact so
    // the user can correct and resend.
    logClientError(source, error, getAccessTokenSilently);
    return false;
  } finally {
    // The shared busy flag only resets for two hardcoded URLs, and neither trade
    // endpoint is one of them, so this ticket owns its own.
    tradeFromScratchStore.getState().setIsSendingTicket(ticketId, false);
  }
}

// LQC-2981 - a ticket opened before local midnight still holds yesterday's trade date.
// useTradeDateRollover rolls open tickets at midnight; this send-time check is the backstop.
export function rollStaleTradeDate<T extends IDatedTicket>(
  ticket: T | undefined,
  update: (changes: Partial<T>) => void,
): T | undefined {
  if (!ticket) return ticket;
  const changes = returnRolledTradeDateChanges(ticket, returnTodayISODate()) as Partial<T> | null;
  if (!changes) return ticket;
  update(changes);
  toast.info(`Trade date rolled to ${returnFormattedDate(changes.tradeDate as string)}.`);
  return { ...ticket, ...changes };
}

// Every open ticket, Leg Creation and draft rows alike. One toast however many rolled.
export function rollAllStaleTradeDates(): number {
  const store = tradeFromScratchStore.getState();
  const today = returnTodayISODate();
  const groups: [Record<string, IDatedTicket>, (id: string, changes: any) => void][] = [
    [store.bondTicketsByTicketId, store.updateBondTicket],
    [store.futureTicketsByTicketId, store.updateFutureTicket],
    [store.bondOptionTicketsByTicketId, store.updateBondOptionTicket],
    [store.swapTicketsByTicketId, store.updateSwapTicket],
    [store.swaptionTicketsByTicketId, store.updateSwaptionTicket],
    [store.repoTicketsByTicketId, store.updateRepoTicket],
  ];
  let rolled = 0;
  for (const [tickets, update] of groups) {
    for (const [ticketId, ticket] of Object.entries(tickets)) {
      const changes = returnRolledTradeDateChanges(ticket, today);
      if (!changes) continue;
      update(ticketId, changes);
      rolled += 1;
    }
  }
  if (rolled > 0) toast.info(`Trade date rolled to ${returnFormattedDate(today)} on open tickets.`);
  return rolled;
}

export async function sendBondTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.bondTicketsByTicketId[ticketId], (changes) =>
    store.updateBondTicket(ticketId, changes),
  );
  if (!ticket) return false;

  // The portfolio carries the entity and LEI, so an unresolvable one blocks the send
  // rather than producing a message with no client details.
  const portfolio = findPortfolioByCode(ticket.portfolioCode);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: BOND_TRADE_ENDPOINT,
    missingFields: returnMissingBondTicketFields(ticket),
    message: buildBondTradeMessage({
      ticket,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentTicketSummary(ticket),
    successMessage: "Bond sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendBondTicket",
    resetTicket: () =>
      tradeFromScratchStore.getState().resetBondTicket(ticketId),
  });
}

export async function sendFutureTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.futureTicketsByTicketId[ticketId], (changes) =>
    store.updateFutureTicket(ticketId, changes),
  );
  if (!ticket) return false;

  const portfolio = findPortfolioByCode(ticket.portfolioCode);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: FUTURE_TRADE_ENDPOINT,
    missingFields: returnMissingFutureTicketFields(ticket),
    message: buildFutureTradeMessage({
      ticket,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentFutureTicketSummary(ticket),
    successMessage: "Future sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendFutureTicket",
    resetTicket: () =>
      tradeFromScratchStore.getState().resetFutureTicket(ticketId),
  });
}

export async function sendBondOptionTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.bondOptionTicketsByTicketId[ticketId], (changes) =>
    store.updateBondOptionTicket(ticketId, changes),
  );
  if (!ticket) return false;

  const portfolio = findPortfolioByCode(ticket.portfolioCode);
  // The underlying carries the currency the message must quote payment in.
  const underlying = findBondSecurityByIsin(ticket.isin);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: BOND_OPTION_TRADE_ENDPOINT,
    missingFields: returnMissingBondOptionTicketFields(ticket),
    message: buildBondOptionTradeMessage({
      ticket,
      underlying,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentBondOptionTicketSummary(ticket),
    successMessage: "Bond Option sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendBondOptionTicket",
    resetTicket: () =>
      tradeFromScratchStore.getState().resetBondOptionTicket(ticketId),
  });
}

export async function sendSwapTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.swapTicketsByTicketId[ticketId], (changes) =>
    store.updateSwapTicket(ticketId, changes),
  );
  if (!ticket) return false;

  const portfolio = findPortfolioByCode(ticket.portfolioCode);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: SWAP_TRADE_ENDPOINT,
    missingFields: returnMissingSwapTicketFields(ticket),
    message: buildSwapTradeMessage({
      ticket,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentSwapTicketSummary(ticket),
    successMessage: "Swap sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendSwapTicket",
    resetTicket: () => tradeFromScratchStore.getState().resetSwapTicket(ticketId),
  });
}

export async function sendSwaptionTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.swaptionTicketsByTicketId[ticketId], (changes) =>
    store.updateSwaptionTicket(ticketId, changes),
  );
  if (!ticket) return false;

  const portfolio = findPortfolioByCode(ticket.portfolioCode);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: SWAPTION_TRADE_ENDPOINT,
    missingFields: returnMissingSwaptionTicketFields(ticket),
    message: buildSwaptionTradeMessage({
      ticket,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentSwaptionTicketSummary(ticket),
    successMessage: "Swaption sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendSwaptionTicket",
    resetTicket: () =>
      tradeFromScratchStore.getState().resetSwaptionTicket(ticketId),
  });
}

export async function sendRepoTicket({
  ticketId,
  user,
  getAccessTokenSilently,
}: ISendBondTicketParams): Promise<boolean> {
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[ticketId]) return false;

  const ticket = rollStaleTradeDate(store.repoTicketsByTicketId[ticketId], (changes) =>
    store.updateRepoTicket(ticketId, changes),
  );
  if (!ticket) return false;

  const portfolio = findPortfolioByCode(ticket.portfolioCode);

  return postTicket({
    ticketId,
    user,
    getAccessTokenSilently,
    endpoint: REPO_TRADE_ENDPOINT,
    missingFields: returnMissingRepoTicketFields(ticket),
    message: buildRepoTradeMessage({
      ticket,
      portfolio,
      traderName: returnTraderName(user),
      fallbackEntity: returnUserEntity(user),
    }),
    summary: returnSentRepoTicketSummary(ticket),
    successMessage: "Repo sent to Trade Creation.",
    source: "tradeFromScratchStoreACTIONS.sendRepoTicket",
    resetTicket: () => tradeFromScratchStore.getState().resetRepoTicket(ticketId),
  });
}

// LQC-2941 - the send for whichever instrument a Trade Creation draft leg holds.
export function sendTicketForInstrument(
  instrument: TicketInstrument,
  params: ISendBondTicketParams,
): Promise<boolean> {
  if (instrument === TicketInstrument.Future) return sendFutureTicket(params);
  if (instrument === TicketInstrument.BondOption) return sendBondOptionTicket(params);
  if (instrument === TicketInstrument.Swap) return sendSwapTicket(params);
  if (instrument === TicketInstrument.Swaption) return sendSwaptionTicket(params);
  if (instrument === TicketInstrument.Repo) return sendRepoTicket(params);
  return sendBondTicket(params);
}
